import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, FormGroup, Label, Input, Button } from "reactstrap";
import { Breadcrumbs } from "../../../../../AbstractElements";
import { useNavigate, useParams } from "react-router-dom";
import { getPlanbyId, addplan, updatePlan } from "../../../../../api-handle/subscriptionApi";
import { sanitizeArabicFieldEvent } from "../../../../../Services/arabicInput";
import "./subscription.css";

const SubscriptionForm = ({ formData, setFormData, onSubmit, onCancel, mainTitle }) => {
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Character limits
  const limits = {
    name: 100,
    nameAr: 100,
    description: 500,
    descriptionAr: 500,
    price: 9999,
    duration: 365,
  };

  // Duration type options
  const durationTypeOptions = [
    { value: "days", label: "Days" },
    { value: "weeks", label: "Weeks" },
    { value: "months", label: "Months" },
    { value: "years", label: "Years" },
  ];

  // Handle input changes
  const handleChange = (e) => {
    const { name } = e.target;
    // Arabic fields (nameAr, descriptionAr) only accept Arabic text
    const value = sanitizeArabicFieldEvent(e);

    // Apply character limits
    if (name === "name" && value.length > limits.name) return;
    if (name === "nameAr" && value.length > limits.nameAr) return;
    if (name === "description" && value.length > limits.description) return;
    if (name === "descriptionAr" && value.length > limits.descriptionAr) return;

    // Apply number limits
    if (name === "price" && value !== "") {
      const numValue = Number(value);
      if (numValue < 0 || numValue > limits.price) return;
    }
    if (name === "duration" && value !== "") {
      const numValue = Number(value);
      if (numValue < 1 || numValue > limits.duration) return;
    }

    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  // Get character count for display
  const getCharacterCount = (fieldName) => {
    const current = formData[fieldName]?.length || 0;
    const max = limits[fieldName];
    return { current, max, remaining: max - current };
  };

  // Validate form fields
  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Plan Name is required";
    if (!formData.nameAr) newErrors.nameAr = "Arabic Plan Name is required";
    if (!formData.description) newErrors.description = "Description is required";
    if (!formData.descriptionAr) newErrors.descriptionAr = "Arabic Description is required";
    if (!formData.duration) newErrors.duration = "Duration is required";
    if (!formData.durationType) newErrors.durationType = "Duration Type is required";
    if (!formData.price) newErrors.price = "Price is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      try {
        await onSubmit(formData);
      } catch (error) {
        setErrors({ ...errors, general: error.message });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Form onSubmit={handleFormSubmit} role="form" aria-label="Subscription plan form" className="custom-form">
      <Row className="form-sections" style={{ justifyContent: "center" }}>
        {/* Basic Information Section */}
        <Col md="6" className="form-section">
          <h6 className="section-header" role="heading" aria-level="3">
            Basic Information
          </h6>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="name" className="required-field">
                  Plan Name (English)
                  <span className="sr-only">Required field</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter Plan Name"
                  maxLength={limits.name}
                  aria-describedby="name-help name-error"
                  aria-required="true"
                  aria-invalid={errors.name ? "true" : "false"}
                />
                <div className="character-count" id="name-help" aria-live="polite">
                  {getCharacterCount("name").current}/{limits.name} characters
                </div>
                {errors.name && (
                  <div className="text-danger small mt-1" id="name-error" role="alert">
                    {errors.name}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="nameAr" className="required-field">
                  Plan Name (Arabic)
                  <span className="sr-only">Required field</span>
                </Label>
                <Input
                  id="nameAr"
                  type="text"
                  name="nameAr"
                  value={formData.nameAr}
                  onChange={handleChange}
                  placeholder="أدخل اسم الخطة"
                  maxLength={limits.nameAr}
                  style={{ direction: "rtl" }}
                  aria-describedby="nameAr-help nameAr-error"
                  aria-required="true"
                  aria-invalid={errors.nameAr ? "true" : "false"}
                />
                <div className="character-count" id="nameAr-help" aria-live="polite">
                  {getCharacterCount("nameAr").current}/{limits.nameAr} characters
                </div>
                {errors.nameAr && (
                  <div className="text-danger small mt-1" id="nameAr-error" role="alert">
                    {errors.nameAr}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="duration" className="required-field">
                  Duration
                  <span className="sr-only">Required field</span>
                </Label>
                <Input
                  id="duration"
                  type="number"
                  name="duration"

                  value={formData.duration}
                  onChange={handleChange}
                  placeholder="Duration"
                  min={1}
                  max={limits.duration}
                  aria-describedby="duration-help duration-error"
                  aria-required="true"
                  aria-invalid={errors.duration ? "true" : "false"}
                />
                <div className="input-help" id="duration-help">
                  Max {limits.duration} units
                </div>
                {errors.duration && (
                  <div className="text-danger small mt-1" id="duration-error" role="alert">
                    {errors.duration}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="durationType" className="required-field">
                  Duration Type
                  <span className="sr-only">Required field</span>
                </Label>
                <Input
                  id="durationType"
                  type="select"
                  name="durationType"
                  value={formData.durationType}
                  onChange={handleChange}
                  aria-describedby="durationType-error"
                  aria-required="true"
                  aria-invalid={errors.durationType ? "true" : "false"}
                >
                  <option value="">Select a duration type</option>
                  {durationTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
                {errors.durationType && (
                  <div className="text-danger small mt-1" id="durationType-error" role="alert">
                    {errors.durationType}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label for="price" className="required-field">
              Price (KWD)
              <span className="sr-only">Required field</span>
            </Label>
            <Input
              id="price"
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              液
              placeholder="Price"
              min={0}
              max={limits.price}
              step="0.01"
              aria-describedby="price-help price-error"
              aria-required="true"
              aria-invalid={errors.price ? "true" : "false"}
            />
            <div className="input-help" id="price-help">
              Max {limits.price} KWD
            </div>
            {errors.price && (
              <div className="text-danger small mt-1" id="price-error" role="alert">
                {errors.price}
              </div>
            )}
          </FormGroup>
        </Col>

        {/* Description Section */}
        <Col md="6" className="form-section description-section">
          <h6 className="section-header" role="heading" aria-level="3">
            Descriptions
          </h6>

          <FormGroup>
            <Label for="description" className="required-field">
              Plan Description (English)
              <span className="sr-only">Required field</span>
            </Label>
            <Input
              id="description"
              type="textarea"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter Plan Description"
              maxLength={limits.description}
              rows="4"
              className="scrollable-textarea"
              aria-describedby="description-help description-error"
              aria-required="true"
              aria-invalid={errors.description ? "true" : "false"}
            />
            <div className="character-count" id="description-help" aria-live="polite">
              {getCharacterCount("description").current}/{limits.description} characters
            </div>
            {errors.description && (
              <div className="text-danger small mt-1" id="description-error" role="alert">
                {errors.description}
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="descriptionAr" className="required-field">
              Plan Description (Arabic)
              <span className="sr-only">Required field</span>
            </Label>
            <Input
              id="descriptionAr"
              type="textarea"
              name="descriptionAr"
              value={formData.descriptionAr}
              onChange={handleChange}
              placeholder="أدخل وصف الخطة"
              maxLength={limits.descriptionAr}
              rows="4"
              className="scrollable-textarea"
              style={{ direction: "rtl" }}
              aria-describedby="descriptionAr-help descriptionAr-error"
              aria-required="true"
              aria-invalid={errors.descriptionAr ? "true" : "false"}
            />
            <div className="character-count" id="descriptionAr-help" aria-live="polite">
              {getCharacterCount("descriptionAr").current}/{limits.descriptionAr} characters
            </div>
            {errors.descriptionAr && (
              <div className="text-danger small mt-1" id="descriptionAr-error" role="alert">
                {errors.descriptionAr}
              </div>
            )}
          </FormGroup>
        </Col>
      </Row>

      {errors.general && (
        <Row style={{ justifyContent: "center" }} className="mt-3">
          <Col md="12">
            <div className="alert alert-danger" role="alert" aria-live="assertive">
              <i className="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
              {errors.general}
            </div>
          </Col>
        </Row>
      )}

      <Row className="mt-4">
        <Col md="12" className="text-end">
          <Button
            color="secondary"
            onClick={onCancel}
            aria-label="Cancel and return"
            className="me-2"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={loading}
            aria-label={loading ? "Saving plan data" : "Save plan data"}
            className="save-btn"
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin me-2" aria-hidden="true"></i>
                Saving...
              </>
            ) : (
              "Save Plan"
            )}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const SubscriptionFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    name: "",
    nameAr: "",
    description: "",
    descriptionAr: "",
    duration: "",
    durationType: "",
    price: "",
  });

  // Fetch existing plan if ID is present
  useEffect(() => {
    if (id) {
      getPlanbyId(id)
        .then((res) => {
          if (res?.plan) {
            setFormData(res.plan);
          }
        })
        .catch((err) => {
          console.error("Error fetching plan:", err);
        });
    } else {
      setFormData({
        name: "",
        nameAr: "",
        description: "",
        descriptionAr: "",
        duration: "",
        durationType: "",
        price: "",
      });
    }
  }, [id]);

  // Handle form submission
  const handleSubmit = async (formData) => {
    try {
      if (id) {
        await updatePlan(id, formData);
        navigate("/subscription/plans/");
      } else {
        await addplan(formData);
        navigate("/subscription/plans/");
      }
    } catch (error) {
      throw error; // Let SubscriptionForm handle the error
    }
  };

  return (
    <Container fluid className="custom-container">
      <Breadcrumbs
        mainTitle={id ? "Edit Subscription Plan" : "Add New Subscription Plan"}
        parent="Subscription Management"
      />
      <Row>
        <Col xl="12">
          <SubscriptionForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onCancel={() => navigate(-1)}
            mainTitle={id ? "Edit Subscription Plan" : "Add New Subscription Plan"}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default SubscriptionFormPage;
